<template>
  <sc-page :title="schema.title" :desc="schema.desc" ref="table">
    <div class="body">
      <widget-search
        :is-hidden="false"
        :form="form"
        :groups="groups"
        @search="onSearch"
      />
      <widget-table-button :buttons="extraButtons">
        <template v-slot:right>
          <a-select placeholder="选择排序" v-model:value="orderType">
            <a-select-option :value="0">创建时间倒序</a-select-option>
            <a-select-option :value="1">创建时间顺序</a-select-option>
          </a-select>
        </template>
      </widget-table-button>
      <div style="display: flex; flex-direction: row; flex-wrap: wrap">
        <div
          class="block"
          :class="{ selected: selectedKeyMap[item.id] }"
          @click="() => toggleSelect(item)"
          v-for="(item, key) in listData.list"
          :key="key"
        >
          <div class="space-between" style="margin-bottom: 8px">
            <div class="row">
              <a-checkbox
                :checked="selectedKeyMap[item.id]"
                style="margin-right: 8px"
              />
              <a-tooltip title="设备分组">
                <div v-if="item.group">{{ item.group?.name }}</div>
                <div v-else style="color: #909399">无分组</div>
              </a-tooltip>
            </div>
            <div class="row" @click.stop>
              <a-tooltip title="切换代理设置">
                <a-switch
                  style="margin-right: 8px"
                  size="small"
                  :checked="item.proxySetting"
                  @update:checked="rowButtons.switchProxySetting(item, $event)"
                />
              </a-tooltip>
              <sc-btn
                :handler="() => rowButtons.edit(item)"
                icon="EditFilled"
                size="small"
                style="margin-right: 8px"
              />
              <!--              <sc-btn icon="DeleteFilled" size="small" />-->
            </div>
          </div>
          <div class="space-between">
            <div class="row" style="min-width: 80px">
              <a-tooltip
                :overlayStyle="{ whiteSpace: 'pre-line' }"
                :title="deviceStatusTip(item)"
              >
                <status-icon
                  :value="deviceStatusValue[item.status]"
                  :size="14"
                />
              </a-tooltip>
              <edit-text
                :value="item.name"
                @click.stop
                :handler="(name: string) => rowButtons.nameEdit(item, name)"
              />
            </div>
            <div class="row">
              <div
                class="tag"
                v-for="(tag, tKey) in item.tags.list"
                :key="tKey"
              >
                {{ tag.name }}
              </div>
            </div>
          </div>
          <div class="row">
            <a-tooltip title="加速包">
              <div
                v-for="(accelerator, tKey) in item.accelerators.list"
                :key="tKey"
              >
                {{ accelerator.name }}
              </div>
              <div
                v-if="item.accelerators.list.length === 0"
                style="color: #909399"
              >
                无加速包
              </div>
            </a-tooltip>
          </div>
          <div class="space-between">
            <a-tooltip title="代理规则">
              <div v-if="item.rule">{{ item.rule?.name }}</div>
              <div v-else style="color: #909399">无代理规则</div>
            </a-tooltip>
            <sc-btn
              icon="FormOutlined"
              type="text"
              :handler="() => rowButtons.proxyEdit(item)"
            />
          </div>
          <div style="height: 66px; overflow-y: auto; overflow-x: hidden">
            <template v-if="item.proxies.list.length > 0">
              <a-tooltip
                placement="right"
                :overlayStyle="{ whiteSpace: 'pre-line' }"
                :title="proxyTip(proxy)"
                v-for="(proxy, pKey) in item.proxies.list"
                :key="pKey"
                @click.stop
              >
                <a-popconfirm
                  ok-text="确定"
                  cancel-text="取消"
                  @confirm="rowButtons.selectProxy(item, proxy)"
                >
                  <template #title>
                    确定要切换到
                    <span style="color: red">{{ proxy.name }}</span> 吗
                  </template>
                  <div
                    :class="{ selected: proxy.id === item.selectedProxy }"
                    class="space-between"
                    style="padding: 0 4px"
                  >
                    <div
                      style="
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                      "
                    >
                      {{ proxy.name }} | {{ proxy.username }} |
                      {{ proxy.typeText }}://{{ proxy.host }}:{{ proxy.port }}
                    </div>
                    <status-icon
                      style="margin-left: 8px"
                      :value="proxyStatusValue[proxy.status]"
                    />
                  </div>
                </a-popconfirm>
              </a-tooltip>
            </template>
            <div v-else style="color: #909399">未分配代理线路</div>
          </div>
        </div>
      </div>
      <!--    分页    -->
      <widget-table-pagination
        :count="listData.count"
        v-model:page-num="tableService.listConfig.pageNum"
        v-model:page-size="tableService.listConfig.pageSize"
      />
      <a-back-top :target="backTopTarget" :visibilityHeight="100" />
    </div>
  </sc-page>
</template>
<script setup lang="ts">
import { ComponentPublicInstance, computed, reactive, ref, shallowRef, watch } from 'vue';
import WidgetTablePagination from '@/components/business/crud/table/WidgetTablePagination.vue';
import WidgetTableButton from '@/components/business/crud/table/WidgetTableButton.vue';
import WidgetSearch from '@/components/business/crud/form/WidgetSearch.vue';
import { createSearchService } from '@/components/business/crud/service/CrudSearchService';
import _ from 'lodash';
import { createCrudTableService } from '@/components/business/crud/service/CrudTableService';
import { useQueryParam } from '@/util/queryParam';
import ScPage from '@/components/common/page/ScPage.vue';
import ScBtn from '@/components/common/widget/ScBtn.vue';
import StatusIcon from '@/components/business/crud/common/StatusIcon.vue';
import { windows } from '@/components/common/window/Windows';
import { request } from '@/util/request';
import { proxyTip } from '@/views/management/common/util';
import HasMany from '@/components/business/crud/widgets/impl/component/HasMany.vue';
import { app } from '@/models/App';
import { useActiveElement, useMagicKeys, whenever } from '@vueuse/core';
import EditText from '@/components/business/crud/common/EditText.vue';

const deviceStatusValue: Record<string, boolean> = {
  online: true,
  error: false,
};

function deviceStatusTip(item: any) {
  if (item.status === 'online') {
    return '设备在线';
  } else if (item.status === 'offline') {
    return '设备离线';
  } else {
    return '设备异常\n' + item.exception;
  }
}

const proxyStatusValue: Record<string, boolean> = {
  normal: true,
  error: false,
};



const searchService = createSearchService('management', 'device');
const { form, groups, getCondition } = searchService.use();
const tableService = createCrudTableService('management', 'device');
const { schema, columns, listData, rowSelection, onChange, buttons } =
  tableService.use();

const extraButtons = computed(() => {
  return [
    buttons.value[0],
    {
      type: 'table',
      text: isSelectedAll.value ? '全消' : '全选',
      tip: '快捷键Ctrl + A',
      icon: isSelectedAll.value ? 'CloseOutlined' : 'CheckOutlined',
      enable: true,
      handler: () => toggleSelectAll(),
    },
    ...buttons.value.slice(1),
  ];
});

const activeElement = useActiveElement();
const notUsingInput = computed(
  () =>
    activeElement.value?.tagName !== 'INPUT' &&
    activeElement.value?.tagName !== 'TEXTAREA'
);
const keys = useMagicKeys({
  passive: false,
  onEventFired(e) {
    if (e.ctrlKey && e.key === 'a' && notUsingInput.value) e.preventDefault();
  },
});

whenever(keys.ctrl_a, () => {
  if (notUsingInput.value) {
    toggleSelectAll();
  }
});
const isSelectedAll = computed(
  () =>
    rowSelection.value?.selectedRowKeys.value.length === listData.list.length
);

function toggleSelectAll() {
  if (isSelectedAll.value) {
    rowSelection.value?.onChange([], []);
  } else {
    rowSelection.value?.onChange([], listData.list);
  }
}

tableService.listConfig.addLoad = ['proxies', 'exception'];
// 处理选中
const selectedKeyMap = computed(() => {
  const value: Record<string, any> = {};
  rowSelection.value?.selectedRowKeys.value.map((i) => (value[i] = i));
  return value;
});

function toggleSelect(row: any) {
  const selectedRows: any[] = [];
  listData.list.map((i) => {
    if (selectedKeyMap.value[i.id] && i.id !== row.id) {
      selectedRows.push(i);
    }
  });
  if (!selectedKeyMap.value[row.id]) {
    selectedRows.push(row);
  }
  rowSelection.value?.onChange([], selectedRows);
}

const searchData = useQueryParam('searchData');
const readonlyFields = useQueryParam('readonlyFields');

/**
 * 查询时候修改列表查询条件，将查询数据同步到url上
 */
function onSearch() {
  tableService.listConfig.condition = getCondition();
  searchData.value = _.cloneDeep(form.data.value);
}

let isInit = false;

const queryParam = useQueryParam('table');
searchService.init().then(() => {
  /**
   * 监听url数据，来修改查询表单数据
   */
  watch(
    searchData,
    () => {
      const queryValue = searchData.value || {};
      if (!_.isEqual(queryValue, form.data.value)) {
        form.data.value = queryValue;
      }
    },
    { deep: true, immediate: true }
  );
  /**
   * 监听url数据，来设置查询表单只读字段
   * todo 双向绑定
   */
  watch(
    () => readonlyFields,
    () => {
      if (readonlyFields.value) {
        searchService.setReadonlyFields(readonlyFields.value);
        if (isInit) {
          onSearch();
        }
      }
    },
    { deep: true, immediate: true }
  );
  /**
   * 监听url数据，来修改列表排序字段
   * todo 双向绑定
   */
  watch(
    () => queryParam.value?.orderBy,
    () => {
      onChange(undefined, undefined, queryParam.value?.orderBy || []);
    },
    { deep: true, immediate: true }
  );
  if (!isInit) {
    isInit = true;
    onSearch();
  }
  tableService.init();
});
const orderType = ref(0)
const orders = reactive([[{ column: 'createTime', order: 'desc' }], [{ column: 'createTime', order: 'asc' }]])
watch(orderType, () => {
  tableService.listConfig.orderBy = orders[orderType.value]
})
const table = ref<ComponentPublicInstance>();

function backTopTarget() {
  return table.value?.$el;
}

const rowButtons = {
  edit: async (row: any) => {
    const id = windows.open({
      content: {
        type: 'route',
        href: `/management/device/update?id=${row.id}`,
      },
      title: '编辑设备',
    });
    windows.getCloseData(id).then(async (res) => {
      if (res) {
        await tableService.refreshList();
      }
    });
  },
  nameEdit: async function (device: any, name: string) {
    const res = await request('/management/device/update', {
      id: device.id,
      data: { name },
    });
    if (res) {
      device.name = name;
      return true;
    }
  },
  proxyEdit: async function (row: any) {
    const id = windows.open({
      content: {
        type: 'component',
        component: shallowRef(HasMany),
        props: {
          propName: 'proxies',
          info: {
            title: '绑定线路',
            status: 'edit',
          },
          widget: app.data.widgetMap.management.device.widgets.proxies.type,
          data: row,
        },
      },
      title: row.name + '绑定线路编辑',
      width: 1280,
      height: 800,
      padding: 20,
    });
    const res = await windows.getCloseData(id);
    if (res) {
      await tableService.refreshList();
    }
  },
  async selectProxy(device: any, proxy: any) {
    request('/management/device/update', {
      id: device.id,
      data: { selectedProxy: proxy.id },
    }).then((res) => {
      if (res) {
        device.selectedProxy = proxy.id;
      }
    });
  },
  switchProxySetting(device: any, value: boolean) {
    const old = device.proxySetting;
    device.proxySetting = value;
    request('/management/device/update', {
      id: device.id,
      data: { proxySetting: value },
    }).then((res) => {
      if (!res) {
        device.proxySetting = old;
      }
    });
  },
};
</script>

<style lang="scss" scoped>
.body {
  padding: $spacing-medium;
}

.block {
  width: 310px;
  padding: 9px;
  border: 1px solid #b6b6b6;
  margin: 0 12px 12px 0;

  &.selected {
    padding: 6px;
    border: 4px solid rgba(29, 165, 122, 1);
  }

  .row {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .space-between {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .selected {
    background: rgba(29, 165, 122, 0.15);
  }

  .tag {
    font-size: $font-size-small;
    font-weight: $font-weight-primary;
    border-radius: $border-radius-medium;
    text-align: center;
    min-width: 40px;
    padding: 1px 3px;
    border: 1px solid #cdcdcd;

    &:not(:last-child) {
      margin-right: 8px;
    }
  }
}

.ant-back-top {
  bottom: 100px;
  right: 100px;
}
</style>
